import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { AppContext } from '../../context/AppContext';
import { UserContext } from '../../context/UserContext';
import { useDesktopMedia } from '../../util/MediaQuery';
import { isGuidedOnboardingEnabled } from '../../util/guidedOnboarding';
import colors from '../../styles/colors';
import {
  GET_CURRENT_ORGANIZATION,
  UPDATE_ONBOARDING_CHECKLIST_PROGRESS,
} from '../../graphql/queries';
import HelpOverlay from './HelpOverlay';
import OnboardingTooltip from './OnboardingTooltip';
import { ONBOARDING_TOURS } from './tourConfig';
import { isStepComplete, organizationSmsCampaignsEnabled } from './checklistConfig';

/** Slow settings loads + delayed paint; missing target sets tourTargetMissStepId and ends the tour. */
const TARGET_MAX_ATTEMPTS = 45;
const TARGET_RETRY_MS = 250;

/** Window, document roots, and scrollable ancestors (admin shell uses overflow:auto, not window). */
function scrollToAbsoluteTop(fromEl) {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  const { documentElement, body } = document;
  if (documentElement) documentElement.scrollTop = 0;
  if (body) body.scrollTop = 0;
  let el = fromEl;
  while (el) {
    if (el.scrollTop) {
      el.scrollTop = 0;
    }
    el = el.parentElement;
  }
}

const HighlightRing = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 1001;
  border: ${(p) => p.$borderWidth || 2}px solid ${colors.seaGreen};
  border-radius: 8px;
  box-shadow: 0 0 0 3px rgba(39, 187, 173, 0.2);
  transition: top 0.15s ease, left 0.15s ease, width 0.15s ease, height 0.15s ease;
`;

export default function OnboardingHighlightTour() {
  const history = useHistory();
  const { app, setApp } = useContext(AppContext);
  const { user } = useContext(UserContext);
  const isDesktop = useDesktopMedia();
  const screenIndex = app?.onboarding?.tourScreenIndex ?? 0;
  const [targetEl, setTargetEl] = useState(null);
  const [ringRect, setRingRect] = useState(null);
  const computeRingRect = useCallback((el) => {
    const r = el.getBoundingClientRect();
    return {
      top: r.top - 10,
      left: r.left - 10,
      width: r.width + 20,
      height: r.height + 20,
      borderWidth: 4,
    };
  }, []);

  const pollTimerRef = useRef(null);
  const routeAppliedRef = useRef('');
  const focusedIdRef = useRef(null);
  const screenIndexRef = useRef(0);

  const open = !!app?.onboarding?.checklistSidebarOpen;
  const focusedId = app?.onboarding?.focusedChecklistStepId;
  const progress = user?.organization?.onboardingChecklistProgress;
  const suppressForStepId = app?.onboarding?.tourSuppressForStepId;
  const missStepId = app?.onboarding?.tourTargetMissStepId;

  focusedIdRef.current = focusedId;

  const tour = focusedId >= 4 && focusedId <= 7
    ? ONBOARDING_TOURS[focusedId]
    : null;
  const screens = tour?.screens ?? [];
  const screen = screens[screenIndex];
  screenIndexRef.current = screenIndex;

  const tourActive = isGuidedOnboardingEnabled(user)
    && isDesktop
    && tour
    && progress
    && focusedId != null
    && suppressForStepId !== focusedId
    && missStepId !== focusedId;

  /** Reset tour chrome when the checklist is dismissed (X); keep state when sidebar closes after a step click (still focused). */
  useEffect(() => {
    if (!open && focusedId == null) {
      setApp((prev) => ({
        ...prev,
        onboarding: {
          ...prev.onboarding,
          tourScreenIndex: 0,
        },
      }));
      setTargetEl(null);
      routeAppliedRef.current = '';
    }
  }, [open, focusedId, setApp]);

  useEffect(() => {
    if (!tourActive || !screen?.enterRoute) return;
    /** Stay on the current URL for step 1 of each tour; first Next applies `enterRoute` for screen 1+. */
    if (screenIndex === 0) return;
    const r = screen.enterRoute;
    const token = `${focusedId}-${screenIndex}-${screen.id}`;
    if (routeAppliedRef.current === token) return;
    routeAppliedRef.current = token;
    const { pathname, search = '', state } = r;
    if (state !== undefined) {
      history.push({ pathname, search, state });
    } else {
      history.push({ pathname, search });
    }
  }, [tourActive, focusedId, screenIndex, screen, history]);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearPoll();
    setTargetEl(null);
    if (!tourActive || !screen) return undefined;

    if (screen.centered) {
      return undefined;
    }

    const selector = screen.targetSelector;
    if (!selector) return undefined;

    const schedule = (attempt) => {
      const el = document.querySelector(selector);
      if (el) {
        setTargetEl(el);
        return;
      }
      if (attempt + 1 >= TARGET_MAX_ATTEMPTS) {
        const fid = focusedIdRef.current;
        const def = fid >= 4 && fid <= 7 ? ONBOARDING_TOURS[fid] : null;
        const len = def?.screens?.length ?? 0;
        const i = screenIndexRef.current;
        /** Never auto-advance the first screen of a step; user must click Next (avoids skipping intro on wrong route). */
        if (i === 0) {
          return;
        }
        if (i < len - 1) {
          routeAppliedRef.current = '';
          setApp((prev) => ({
            ...prev,
            onboarding: {
              ...prev.onboarding,
              tourScreenIndex: i + 1,
            },
          }));
        } else {
          setApp((prev) => ({
            ...prev,
            onboarding: {
              ...prev.onboarding,
              tourTargetMissStepId: fid,
            },
          }));
        }
        return;
      }
      pollTimerRef.current = setTimeout(
        () => schedule(attempt + 1),
        TARGET_RETRY_MS,
      );
    };

    schedule(0);
    return clearPoll;
  }, [tourActive, screen, screenIndex, clearPoll, setApp]);

  useEffect(() => {
    if (!tourActive || !targetEl || screen?.centered) {
      return undefined;
    }
    if (screen.scrollWindowToTop) {
      scrollToAbsoluteTop(targetEl);
      return undefined;
    }
    if (!screen?.scrollIntoView) {
      return undefined;
    }
    const block = screen.scrollIntoViewBlock ?? 'center';
    targetEl.scrollIntoView({ block, inline: 'nearest' });
    return undefined;
  }, [
    tourActive,
    targetEl,
    screen?.centered,
    screen?.scrollIntoView,
    screen?.scrollIntoViewBlock,
    screen?.scrollWindowToTop,
  ]);

  useEffect(() => {
    if (!targetEl || screen?.centered) {
      setRingRect(null);
      return undefined;
    }
    const update = () => {
      setRingRect(computeRingRect(targetEl));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [targetEl, screen, computeRingRect]);

  const [runUpdate, { loading: saving }] = useMutation(
    UPDATE_ONBOARDING_CHECKLIST_PROGRESS,
    {
      refetchQueries: [{ query: GET_CURRENT_ORGANIZATION }],
      awaitRefetchQueries: true,
    },
  );

  const handlePrimary = useCallback(async () => {
    if (!screen) return;
    if (screen.isFinal) {
      const alreadyComplete = isStepComplete(focusedIdRef.current, progress);
      if (!alreadyComplete) {
        const key = tour.completionInputKey;
        const input = { [key]: true };
        try {
          await runUpdate({ variables: { input } });
        } catch (e) {
          console.error(e);
          return;
        }
      }
      const completedStepId = focusedIdRef.current;
      const bumpCompleteModal = completedStepId === 7
        || (completedStepId === 6 && !organizationSmsCampaignsEnabled(user?.organization));
      setApp((prev) => ({
        ...prev,
        onboarding: {
          ...prev.onboarding,
          checklistSidebarOpen: false,
          focusedChecklistStepId: null,
          continueWidgetAfterStepId: completedStepId,
          tourTargetMissStepId: null,
          tourScreenIndex: 0,
          onboardingCompleteModalBump: bumpCompleteModal
            ? (prev.onboarding.onboardingCompleteModalBump ?? 0) + 1
            : (prev.onboarding.onboardingCompleteModalBump ?? 0),
        },
      }));
      return;
    }
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        tourScreenIndex: (prev.onboarding.tourScreenIndex ?? 0) + 1,
      },
    }));
    routeAppliedRef.current = '';
  }, [screen, tour, progress, runUpdate, setApp, user?.organization]);

  const handleBack = useCallback(() => {
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        tourScreenIndex: Math.max(0, (prev.onboarding.tourScreenIndex ?? 0) - 1),
      },
    }));
    routeAppliedRef.current = '';
  }, [setApp]);

  const handleClose = useCallback(() => {
    const fid = focusedIdRef.current;
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        tourSuppressForStepId: fid,
      },
    }));
  }, [setApp]);

  if (!tourActive || !screen) {
    return null;
  }

  return (
    <>
      <HelpOverlay />
      {ringRect && (
        <HighlightRing
          $borderWidth={ringRect.borderWidth}
          style={{
            top: ringRect.top,
            left: ringRect.left,
            width: ringRect.width,
            height: ringRect.height,
          }}
        />
      )}
      <OnboardingTooltip
        title={screen.title}
        body={screen.body}
        currentScreenIndex={screenIndex}
        totalScreens={screens.length}
        primaryLabel={saving ? 'Saving…' : screen.primaryLabel}
        onPrimary={handlePrimary}
        primaryDisabled={saving}
        showBack={screenIndex > 0}
        onBack={handleBack}
        onClose={handleClose}
        targetElement={screen.centered ? null : targetEl}
        showCentered={!!screen.centered}
        placement={screen.placement || 'bottom'}
        pointerPosition={screen.pointerPosition || 'center'}
      />
    </>
  );
}
